"use client";

import { useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import {
  LIVE_STATUSES,
  NOT_STARTED_STATUS,
  FINISHED_STATUSES,
  CANCEL_STATUS,
} from "@/contants";
import { formatCorrectScoresForGrid, splitCorrectScoresListToSections } from "@/utils/matchInfo";
import { formatHandicapLine } from "@/utils/format";
import { settleMatchMarkets } from "../settleMarkets";
import BoltKbvMatchMinute from "../BoltKbvMatchMinute";
import { BOLT_V26_COLS, BOLT_V26_KETQUA_COLS } from "../BoltMatchLeagues";
import BoltOtherBetsDropdown from "../BoltOtherBetsDropdown/BoltOtherBetsDropdown";
import ViuGoTipsAiModal from "../../ViuGoTipsAiModal";
import ICON_ARROW_UP from "../../../assets/images/arrow-up.svg";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import ICON_CORNER from "@/assets/images/kbv-corner.svg";
import "./index.css";

dayjs.extend(utc);
dayjs.extend(timezone);

function TeamSideStats({ yellow, red, corner, showCorner }) {
  const y = Number(yellow) || 0;
  const r = Number(red) || 0;
  const showY = y > 0;
  const showR = r > 0;
  const showC = Boolean(showCorner);
  if (!showY && !showR && !showC) return null;
  return (
    <span className="kbv-v26-team-stats">
      {showY ? (
        <span className="kbv-v26-card kbv-v26-card--yellow" title="Thẻ vàng">
          {y}
        </span>
      ) : null}
      {showR ? (
        <span className="kbv-v26-card kbv-v26-card--red" title="Thẻ đỏ">
          {r}
        </span>
      ) : null}
      {showC ? (
        <span className="kbv-v26-corner" title="Phạt góc">
          <img src={ICON_CORNER} alt="" />
          <span>{Number(corner) || 0}</span>
        </span>
      ) : null}
    </span>
  );
}

export default function BoltMatch({ match, index, isKetqua = false }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
    isHomeWin,
    isAwayWin,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const isEven = index % 2 === 0;
  const bgColor = isEven ? "md:bg-[var(--theme-bg)]" : "md:bg-[var(--theme-bg-muted)]";

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const isNotStarted = NOT_STARTED_STATUS.includes(statusNum);
  const showScore = !isNotStarted;
  const isSettledFinished =
    FINISHED_STATUSES.includes(statusNum) &&
    !CANCEL_STATUS.includes(statusNum);
  const isFinishedResult = isKetqua && isSettledFinished;
  const highlightHomeName = isSettledFinished && isHomeWin;
  const highlightAwayName = isSettledFinished && isAwayWin;
  const teamNameClass = (isWin) =>
    isSettledFinished ? (isWin ? "kbv-v26-team-win" : "font-normal") : "font-bold";
  const matchCols = isKetqua ? BOLT_V26_KETQUA_COLS : BOLT_V26_COLS;

  const homeHtRaw = liveScore?.homeHalfScore ?? match?.homeHalfScore;
  const awayHtRaw = liveScore?.awayHalfScore ?? match?.awayHalfScore;
  const hasHtScore = homeHtRaw != null && homeHtRaw !== "" && awayHtRaw != null && awayHtRaw !== "";

  const ftMarkets = isFinishedResult
    ? settleMatchMarkets({
        homeScore: currentHomeScore,
        awayScore: currentAwayScore,
        handicapLine: handicapValue,
        handicapHomeOdds: homeHandicapOdds,
        handicapAwayOdds: awayHandicapOdds,
        overUnderLine: overUnderValue,
        overOdds,
        underOdds,
        home1x2Odds: homeEuropeOdds,
        drawOdds: instantDrawOdds,
        away1x2Odds: awayEuropeOdds,
      })
    : { hdc: null, ou: null, oneXTwo: null };

  const htMarkets =
    isFinishedResult && hasHtScore
      ? settleMatchMarkets({
          homeScore: currentHomeHalfScore,
          awayScore: currentAwayHalfScore,
          handicapLine: handicapHalfValue,
          handicapHomeOdds: homeHandicapHalfOdds,
          handicapAwayOdds: awayHandicapHalfOdds,
          overUnderLine: overUnderHalfValue,
          overOdds: overHalfOdds,
          underOdds: underHalfOdds,
          home1x2Odds: homeEuropeHalfOdds,
          drawOdds: drawHalfOdds,
          away1x2Odds: awayEuropeHalfOdds,
        })
      : { hdc: null, ou: null, oneXTwo: null };

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const isOddsUp = (trend) =>
    trend === "increased" || trend === "up" || trend === 1 || trend === "1";
  const isOddsDown = (trend) =>
    trend === "decreased" || trend === "down" || trend === 2 || trend === "2";

  const getOddsColor = (trend) => {
    if (isOddsUp(trend)) return "kbv-v26-odds-up";
    if (isOddsDown(trend)) return "kbv-v26-odds-down";
    return "kbv-v26-odds-default";
  };

  const getMobileOddsColor = (trend) => {
    if (isOddsUp(trend)) return "kbv-v26-odds-m-up";
    if (isOddsDown(trend)) return "kbv-v26-odds-m-down";
    return "kbv-v26-odds-default";
  };

  const renderTrendArrow = (trend) => {
    if (isOddsUp(trend)) {
      return (
        <span
          className="shrink-0 inline-block w-[10px] h-[10px] lu-odds-status--up"
          aria-hidden="true"
        />
      );
    }
    if (isOddsDown(trend)) {
      return (
        <span
          className="shrink-0 inline-block w-[10px] h-[10px] lu-odds-status--down"
          aria-hidden="true"
        />
      );
    }
    return null;
  };

  const renderOddsSide = (value, trend, winner, side, compact = false) => {
    const base = compact
      ? "flex items-center justify-center text-center"
      : "min-w-0 w-full flex items-center justify-center";
    const settle =
      winner === "push"
        ? "kbv-v26-odds-push"
        : winner && winner === side
          ? "kbv-v26-odds-win"
          : "";
    const color = settle
      ? ""
      : compact
        ? getMobileOddsColor(trend)
        : getOddsColor(trend);
    const Tag = compact ? "div" : "span";
    const body = (
      <>
        {value || "-"}
        {renderTrendArrow(trend)}
      </>
    );
    return (
      <Tag className={`${base} ${color}`.trim()}>
        {settle ? <span className={settle}>{body}</span> : body}
      </Tag>
    );
  };

  const handleOtherOddsClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;
    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const correctScoresListFull = otherOddsData?.correctScoresOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds) : [];
  const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds) ? otherOddsData.totalCornersOdds : [];
  const hasOtherData = (otherOddsData?.goalOddEvenOdds?.length > 0) || (correctScoresListFull.length > 0) || (totalCornersOdds.length > 0);

  const TZ = "Asia/Ho_Chi_Minh";
  const matchTimeStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("HH:mm")
      : "--";
  const matchDateStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("DD/MM/YYYY")
      : "--";
  const matchDateShort =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("DD/MM")
      : "--";

  const derivedLeagueName = match?.league?.nameVN || match?.league?.name || match?.leagueName || "";
  const derivedLeagueLogo = match?.league?.logo || match?.leagueLogo || "";
  const homeName = match?.homeTeam?.nameVi || match?.homeName;
  const awayName = match?.awayTeam?.nameVi || match?.awayName;
  const scoreText = `${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`;
  const modalStatusLabel = isFinishedResult ? "Kết thúc" : !isLive ? "Sắp diễn ra" : "Trực tiếp";

  return (
    <div className={`w-full transition-all ${bgColor} hover:bg-[var(--theme-accent)]`}>
      <div className={`hidden md:grid ${matchCols} items-center w-full min-w-0 gap-x-[20px] px-2 pt-3 pb-2`}>
        <div className="flex flex-col items-center justify-center gap-4 h-full">
          {isLive ? (
            <div className="flex flex-col items-center gap-5">
              <span className="flex items-center gap-1 kbv-v26-text-red font-bold text-xs">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full kbv-v26-bg-red opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full kbv-v26-bg-red" />
                </span>
                Live
              </span>
              <BoltKbvMatchMinute
                liveScore={liveScore}
                status={statusNum}
                matchTime={match?.matchTime}
                halfStartTime={match?.halfStartTime}
                className="text-[var(--theme-primary)] text-sm font-bold"
              />
            </div>
          ) : (
            <>
              <span className="text-sm font-bold text-[var(--theme-text)] leading-tight">{matchTimeStr}</span>
              <span className="text-sm font-normal text-[var(--theme-text)] leading-tight">{matchDateStr}</span>
            </>
          )}
        </div>

        <div className="flex flex-col justify-center gap-2 overflow-hidden h-full min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            <img src={homeLogo || ""} alt="" className="w-6 h-6 object-contain rounded-full bg-[var(--theme-accent)] p-0.5 shrink-0" />
            <span className={`text-sm text-[var(--theme-text)] truncate min-w-0 flex-1 ${teamNameClass(highlightHomeName)}`}>
              {match?.homeName}
            </span>
            <TeamSideStats
              yellow={homeYellow}
              red={homeRed}
              corner={currentHomeCorner}
              showCorner={showScore}
            />
            {showScore && (
              <span className="text-sm font-bold text-[var(--theme-primary)] shrink-0">{currentHomeScore}</span>
            )}
          </div>
          <div className="flex items-center gap-1 min-w-0">
            <img src={awayLogo || ""} alt="" className="w-6 h-6 object-contain rounded-full bg-[var(--theme-accent)] p-0.5 shrink-0" />
            <span className={`text-sm text-[var(--theme-text)] truncate min-w-0 flex-1 ${teamNameClass(highlightAwayName)}`}>
              {match?.awayName}
            </span>
            <TeamSideStats
              yellow={awayYellow}
              red={awayRed}
              corner={currentAwayCorner}
              showCorner={showScore}
            />
            {showScore && (
              <span className="text-sm font-bold text-[var(--theme-primary)] shrink-0">{currentAwayScore}</span>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-col justify-center gap-2 h-full uppercase">
          <div className="grid grid-cols-3 items-center gap-x-1 min-w-0 w-full text-sm font-normal h-[28px]">
            {renderOddsSide(homeHandicapOdds, homeHandicapTrend, ftMarkets.hdc, "home")}
            <span className="min-w-0 w-full text-center kbv-v26-odds-value">{formatHandicapLine(handicapValue)}</span>
            {renderOddsSide(awayHandicapOdds, awayHandicapTrend, ftMarkets.hdc, "away")}
          </div>
          <div className="grid grid-cols-3 items-center gap-x-1 min-w-0 w-full text-sm font-normal h-[28px]">
            {renderOddsSide(homeHandicapHalfOdds, homeHandicapHalfTrend, htMarkets.hdc, "home")}
            <span className="min-w-0 w-full text-center kbv-v26-odds-value">{formatHandicapLine(handicapHalfValue)}</span>
            {renderOddsSide(awayHandicapHalfOdds, awayHandicapHalfTrend, htMarkets.hdc, "away")}
          </div>
        </div>

        <div className="flex min-w-0 flex-col justify-center gap-2 h-full uppercase">
          <div className="grid grid-cols-3 items-center gap-x-1 min-w-0 w-full text-sm font-normal h-[28px]">
            {renderOddsSide(overOdds, overTrend, ftMarkets.ou, "over")}
            <span className="min-w-0 w-full text-center kbv-v26-odds-value">{overUnderValue ? `${overUnderValue}` : "-"}</span>
            {renderOddsSide(underOdds, underTrend, ftMarkets.ou, "under")}
          </div>
          <div className="grid grid-cols-3 items-center gap-x-1 min-w-0 w-full text-sm font-normal h-[28px]">
            {renderOddsSide(overHalfOdds, overHalfTrend, htMarkets.ou, "over")}
            <span className="min-w-0 w-full text-center kbv-v26-odds-value">{overUnderHalfValue ? `${overUnderHalfValue}` : "-"}</span>
            {renderOddsSide(underHalfOdds, underHalfTrend, htMarkets.ou, "under")}
          </div>
        </div>

        <div className="kbv-v26-market-1x2 flex min-w-0 flex-col justify-center gap-2 h-full uppercase overflow-visible">
          <div className="grid grid-cols-3 items-center gap-x-1 min-w-0 w-full text-sm font-normal h-[28px]">
            {renderOddsSide(homeEuropeOdds, homeEuropeTrend, ftMarkets.oneXTwo, "home")}
            {renderOddsSide(instantDrawOdds, drawTrend, ftMarkets.oneXTwo, "draw")}
            {renderOddsSide(awayEuropeOdds, awayEuropeTrend, ftMarkets.oneXTwo, "away")}
          </div>
          <div className="grid grid-cols-3 items-center gap-x-1 min-w-0 w-full text-sm font-normal h-[28px]">
            {renderOddsSide(homeEuropeHalfOdds, homeEuropeHalfTrend, htMarkets.oneXTwo, "home")}
            {renderOddsSide(drawHalfOdds, drawHalfTrend, htMarkets.oneXTwo, "draw")}
            {renderOddsSide(awayEuropeHalfOdds, awayEuropeHalfTrend, htMarkets.oneXTwo, "away")}
          </div>
        </div>

        {isKetqua ? null : <div className="h-full" />}
      </div>

      {!isKetqua ? (
      <div className="hidden md:flex justify-end gap-2 px-2 pb-2">
        <button
          type="button"
          onClick={handleOpenTipsAiModal}
          className="kbv-v26-tips-ai-btn flex items-center gap-1.5 px-3 py-1 rounded-[4px] text-[var(--theme-on-primary)]"
        >
          <img src={viugoTipsAiSparkle} alt="" className="kbv-v26-tips-ai-sparkle h-3.5 w-3.5 shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-tight">Tips AI</span>
        </button>
        <div
          onClick={handleOtherOddsClick}
          className="kbv-v26-other-bets-btn flex items-center gap-1 bg-[var(--theme-primary)]/10 px-2 py-0.5 rounded-full border border-[var(--theme-primary)]/20 shadow-sm cursor-pointer transition-all"
        >
          <span className="text-sm font-bold text-[var(--theme-text-muted)] tracking-wide">
            {showOther ? "Thu gọn" : "Kèo khác"}
          </span>
          <img
            src={ICON_ARROW_UP}
            alt=""
            className={`kbv-v26-other-bets-arrow block w-5 h-5 opacity-70 transition-all ${showOther ? "rotate-180" : ""}`}
          />
        </div>
      </div>
      ) : null}

      <div className="flex md:hidden flex-col items-center justify-center py-[6px] w-full bg-transparent">
        <div className="kbv-v26-mobile-card w-full h-fit rounded-[9px] pb-0 flex flex-col relative overflow-hidden">
          <div className="flex justify-between items-center text-[var(--theme-text)] px-[2px] pt-[9px] pb-[4px] gap-[4px]">
            <div className="flex flex-col justify-center items-center w-[95px] h-auto min-w-0">
              <img src={homeLogo} alt="logo" className="w-[26px] h-[26px] mb-[9px] object-contain" />
              <div className={`kbv-v26-mobile-team text-sm leading-[18px] text-center break-all w-full relative line-clamp-2 text-[var(--theme-text)] ${teamNameClass(highlightHomeName)}`}>
                {match?.homeName || "Team"}
              </div>
              <TeamSideStats
                yellow={homeYellow}
                red={homeRed}
                corner={currentHomeCorner}
                showCorner={showScore}
              />
            </div>

            <div className="flex flex-col items-center justify-center text-sm font-bold min-w-[135px] leading-[18px] gap-[4px]">
              {isLive ? (
                <div className="flex justify-between items-center text-[var(--theme-text)] text-x1s font-normal max-w-[140px] w-full h-[20px] leading-4">
                  <div className="flex justify-center items-center kbv-v26-live-bg kbv-v26-live-text w-[20px] h-[20px] rounded-[2px] font-bold">{currentHomeScore ?? "-"}</div>
                  <div className="flex flex-col justify-center items-center gap-[1px] min-w-[43px] h-[20px]">
                    <BoltKbvMatchMinute
                      liveScore={liveScore}
                      status={statusNum}
                      matchTime={match?.matchTime}
                      halfStartTime={match?.halfStartTime}
                      className="kbv-v26-text-red text-sm font-bold leading-[18px]"
                    />
                  </div>
                  <div className="flex justify-center items-center kbv-v26-live-bg kbv-v26-live-text w-[20px] h-[20px] rounded-[2px] font-bold">{currentAwayScore ?? "-"}</div>
                </div>
              ) : isKetqua && showScore ? (
                <div className="flex flex-col items-center justify-center text-[var(--theme-text)] leading-[18px] gap-[2px]">
                  <span className="kbv-v26-mobile-time text-sm font-bold">{matchTimeStr}</span>
                  <span className="kbv-v26-mobile-score text-base font-bold text-[var(--theme-primary)]">
                    {currentHomeScore ?? 0} - {currentAwayScore ?? 0}
                  </span>
                  <span className="kbv-v26-mobile-date text-xs font-normal text-[var(--theme-text-muted)]">{matchDateStr}</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-[var(--theme-text)] leading-[18px]">
                  <span className="kbv-v26-mobile-time text-sm font-bold">{matchTimeStr}</span>
                  <span className="kbv-v26-mobile-date text-xs font-normal text-[var(--theme-text-muted)]">{matchDateStr}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col justify-center items-center w-[95px] h-auto min-w-0">
              <img src={awayLogo} alt="logo" className="w-[26px] h-[26px] mb-[9px] object-contain" />
              <div className={`kbv-v26-mobile-team text-sm leading-[18px] text-center break-all w-full relative line-clamp-2 text-[var(--theme-text)] ${teamNameClass(highlightAwayName)}`}>
                {match?.awayName || "Team"}
              </div>
              <TeamSideStats
                yellow={awayYellow}
                red={awayRed}
                corner={currentAwayCorner}
                showCorner={showScore}
              />
            </div>
          </div>

          <div className="kbv-v26-mobile-odds text-[var(--theme-text)] text-sm leading-5 px-[2px] pb-[9px] pt-[4px] flex flex-col">
            <div className="flex justify-between items-center mb-[4px]">
              <p className="kbv-v26-mobile-odds-label text-[var(--theme-text-muted)] text-x1s leading-[13px] w-[112px] max-[400px]:w-[100px] text-center m-0 p-0">Kèo Chấp (HT/FT)</p>
              <p className="kbv-v26-mobile-odds-label text-[var(--theme-text-muted)] text-x1s leading-[13px] w-[112px] max-[400px]:w-[100px] text-center m-0 p-0">Kèo Tài Xỉu (HT/FT)</p>
              <p className="kbv-v26-mobile-odds-label text-[var(--theme-text-muted)] text-x1s leading-[13px] w-[112px] max-[400px]:w-[100px] text-center m-0 p-0">1x2 (HT/FT)</p>
            </div>

            <div className="flex justify-between items-center mb-[4px]">
              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                {renderOddsSide(homeHandicapOdds, homeHandicapTrend, ftMarkets.hdc, "home", true)}
                <div className="kbv-v26-odds-value text-center">{formatHandicapLine(handicapValue)}</div>
                {renderOddsSide(awayHandicapOdds, awayHandicapTrend, ftMarkets.hdc, "away", true)}
              </div>
              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                {renderOddsSide(overOdds, overTrend, ftMarkets.ou, "over", true)}
                <div className="kbv-v26-odds-value text-center">{overUnderValue ? `${overUnderValue}` : "-"}</div>
                {renderOddsSide(underOdds, underTrend, ftMarkets.ou, "under", true)}
              </div>
              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                {renderOddsSide(homeEuropeOdds, homeEuropeTrend, ftMarkets.oneXTwo, "home", true)}
                {renderOddsSide(instantDrawOdds, drawTrend, ftMarkets.oneXTwo, "draw", true)}
                {renderOddsSide(awayEuropeOdds, awayEuropeTrend, ftMarkets.oneXTwo, "away", true)}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                {renderOddsSide(homeHandicapHalfOdds, homeHandicapHalfTrend, htMarkets.hdc, "home", true)}
                <div className="kbv-v26-odds-value text-center">{formatHandicapLine(handicapHalfValue)}</div>
                {renderOddsSide(awayHandicapHalfOdds, awayHandicapHalfTrend, htMarkets.hdc, "away", true)}
              </div>

              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                {renderOddsSide(overHalfOdds, overHalfTrend, htMarkets.ou, "over", true)}
                <div className="kbv-v26-odds-value text-center">{overUnderHalfValue ? `${overUnderHalfValue}` : "-"}</div>
                {renderOddsSide(underHalfOdds, underHalfTrend, htMarkets.ou, "under", true)}
              </div>

              <div className="flex justify-between items-center w-[112px] max-[400px]:w-[100px]">
                {renderOddsSide(homeEuropeHalfOdds, homeEuropeHalfTrend, htMarkets.oneXTwo, "home", true)}
                {renderOddsSide(drawHalfOdds, drawHalfTrend, htMarkets.oneXTwo, "draw", true)}
                {renderOddsSide(awayEuropeHalfOdds, awayEuropeHalfTrend, htMarkets.oneXTwo, "away", true)}
              </div>
            </div>
          </div>

          {!isKetqua ? (
          <div className="flex items-center justify-end gap-2 px-3 pb-2 pt-1 border-t border-[var(--theme-border)]">
            <button
              type="button"
              onClick={handleOpenTipsAiModal}
              className="kbv-v26-tips-ai-btn flex items-center gap-1 px-2 py-0.5 rounded-[4px] text-[var(--theme-on-primary)]"
            >
              <img src={viugoTipsAiSparkle} alt="" className="kbv-v26-tips-ai-sparkle h-3 w-3 shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-tight">Tips AI</span>
            </button>
            <div
              onClick={handleOtherOddsClick}
              className="kbv-v26-other-bets-btn flex items-center gap-1 bg-[var(--theme-primary)]/15 px-2 py-0.5 rounded-full border border-[var(--theme-primary)]/30 shadow-sm hover:scale-105 transition-all cursor-pointer"
            >
              <span className="text-[10px] font-bold text-[var(--theme-text)] tracking-wide">
                {showOther ? "Thu gọn" : "Kèo khác"}
              </span>
              <img
                src={ICON_ARROW_UP}
                alt=""
                className={`kbv-v26-other-bets-arrow block w-2.5 h-2.5 opacity-80 transition-transform ${showOther ? "rotate-180" : ""}`}
              />
            </div>
          </div>
          ) : null}
        </div>
      </div>

      {!isKetqua ? (
      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{ formattedDate: matchDateShort, formattedGio: matchTimeStr, isLive }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow,
          homeRed,
          awayYellow,
          awayRed,
          currentHomeCorner,
          currentAwayCorner,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={derivedLeagueName}
        leagueLogo={derivedLeagueLogo}
        homeName={homeName}
        homeLogo={homeLogo}
        awayName={awayName}
        awayLogo={awayLogo}
        matchDate={matchDateShort}
        matchTime={matchTimeStr}
        matchTimestamp={match?.matchTime}
        headerScore={showScore ? scoreText : undefined}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={modalStatusLabel}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
      ) : null}

      {!isKetqua && showOther ? (
        <BoltOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          totalCornersOdds={totalCornersOdds}
          odds={{ ...filterOdds, ...liveOdds }}
          showOther={showOther}
        />
      ) : null}
    </div>
  );
}
